import React, { useState, useEffect, useRef } from 'react';
import { getFestivalTheme } from '../utils/festivalTheme';
import './FestivalBanner.css';

// ─────────────────────────────────────────────
//  Generate particle positions (stable, not random on every render)
// ─────────────────────────────────────────────
function makeParticles(emojis, count = 12) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    particles.push({
      id: i,
      emoji: emojis[i % emojis.length],
      left: `${5 + (i * 8.5) % 90}%`,
      delay: `${(i * 0.7) % 6}s`,
      duration: `${4 + (i * 1.1) % 5}s`,
      size: `${11 + (i * 2) % 8}px`,
    });
  }
  return particles;
}

// ─────────────────────────────────────────────
//  Grand overlay particles (Deepavali / Family Day)
// ─────────────────────────────────────────────
function GrandOverlay({ theme }) {
  const particles = makeParticles(theme.particles, 20);

  return (
    <div className="festival-grand-overlay">
      {particles.map(p => (
        <span
          key={p.id}
          className="grand-particle"
          style={{
            left: p.left,
            top: `-20px`,
            fontSize: `${14 + (p.id * 3) % 14}px`,
            animationDelay: p.delay,
            animationDuration: `${6 + (p.id * 1.3) % 6}s`,
          }}
        >
          {p.emoji}
        </span>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tricolor bar for Republic / Independence Day
// ─────────────────────────────────────────────
function TricolorBar() {
  return <div className="festival-tricolor-bar" />;
}

// ─────────────────────────────────────────────
//  Main Banner
// ─────────────────────────────────────────────
export default function FestivalBanner() {
  const theme = getFestivalTheme();
  const [visible, setVisible] = useState(true);
  const particles = theme.active ? makeParticles(theme.particles || theme.emoji, 14) : [];

  useEffect(() => {
    setVisible(true); // reset on theme change
  }, [theme?.name]);

  if (!theme.active || !visible) return null;

  const isTricolor = ['Republic Day', 'Independence Day'].includes(theme.name);
  const isGrand = theme.isGrandFestival || theme.isDWBSpecial;

  return (
    <>
      {/* Grand falling particles (Deepavali / Family Day) */}
      {isGrand && <GrandOverlay theme={theme} />}

      <div
        className="festival-banner"
        style={{ background: theme.bannerBg }}
        role="banner"
        aria-label={`${theme.name} celebration banner`}
      >
        {/* Decorative orbs */}
        <div className="festival-orb festival-orb--1" />
        <div className="festival-orb festival-orb--2" />
        <div className="festival-orb festival-orb--3" />

        {/* Floating particles inside banner */}
        <div className="festival-particles" aria-hidden="true">
          {particles.map(p => (
            <span
              key={p.id}
              className="festival-particle"
              style={{
                left: p.left,
                bottom: '-4px',
                fontSize: p.size,
                animationDelay: p.delay,
                animationDuration: p.duration,
              }}
            >
              {p.emoji}
            </span>
          ))}
        </div>

        {/* Tricolor bottom bar for national days */}
        {isTricolor && <TricolorBar />}

        {/* Scrolling marquee text */}
        {/* Festival image icon — left side */}
        <div style={{
          fontSize: 30, flexShrink: 0, zIndex: 5, margin: '0 8px 0 12px',
          animation: 'festEmojiSpin 3s ease-in-out infinite',
          filter: `drop-shadow(0 0 10px ${theme.glowColor})`,
        }}>
          {theme.emoji[0]}
        </div>

        <div className="festival-inner">
          <div className="festival-track">

            {/* First copy */}
            <span className="festival-emoji">{theme.emoji[0]}</span>

            {/* DWB badge for company-specific days */}
            {theme.isDWBSpecial && (
              <span className="festival-dwb-badge">DWB</span>
            )}

            <span
              className="festival-text"
              style={{
                background: theme.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                '--fest-glow': theme.glowColor,
              }}
            >
              {theme.banner}
            </span>

            <span className="festival-emoji">{theme.emoji[1]}</span>
            <span className="festival-emoji">{theme.emoji[2]}</span>

            {/* Separator */}
            <span className="festival-sep">✦</span>

            {/* Second copy (for seamless loop) */}
            <span className="festival-emoji">{theme.emoji[0]}</span>

            {theme.isDWBSpecial && (
              <span className="festival-dwb-badge">DWB</span>
            )}

            <span
              className="festival-text"
              style={{
                background: theme.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                '--fest-glow': theme.glowColor,
              }}
            >
              {theme.banner}
            </span>

            <span className="festival-emoji">{theme.emoji[1]}</span>
            <span className="festival-emoji">{theme.emoji[2]}</span>

            <span className="festival-sep">✦</span>
          </div>
        </div>

        {/* Close button */}
        <button
          className="festival-close"
          onClick={() => setVisible(false)}
          aria-label="Close festival banner"
        >

        </button>
        {theme.image && (
          <div style={{
            fontSize: 30, flexShrink: 0, margin: '0 8px 0 12px',
            filter: `drop-shadow(0 0 10px ${theme.glowColor})`
          }}>
            {theme.image}
          </div>
        )}
      </div>
    </>
  );
}